import { useState } from "react";
import { api } from "../api/client";
import { useData } from "../hooks/useData";
import { I } from "./Icons";
import { clsx } from "./Primitives";

interface ApiKey {
  id: string; name: string; source: string; scope: "server" | "form"; allowed_origins: string[]; key_prefix: string;
  created_at: string; last_used_at: string | null; use_count: number; revoked_at: string | null;
  created_by_name: string | null; lead_count: number;
}
interface NewKey { id: string; key: string; name: string; source: string; scope: "server" | "form"; allowed_origins: string[] }

const SOURCES = ["info.saga-advisory.com", "www.saga-advisory.com"];
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "never");

function Copy({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <button className="btn ghost sm" onClick={async () => {
      try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* clipboard blocked */ }
    }}>{done ? "Copied" : "Copy"}</button>
  );
}

export default function ApiKeys() {
  const { data: keys, refetch } = useData<ApiKey[]>("/api/admin/api-keys");
  const [form, setForm] = useState({ name: "", source: SOURCES[0], scope: "server" as "server" | "form", origins: "" });
  const [adding, setAdding] = useState(false);
  const [created, setCreated] = useState<NewKey | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const base = `${window.location.origin}/api/v1`;

  const create = async () => {
    setErr(null);
    try {
      const k = await api.post<NewKey>("/api/admin/api-keys", {
        name: form.name, source: form.source, scope: form.scope,
        allowed_origins: form.origins.split(/[\s,]+/).filter(Boolean),
      });
      setCreated(k); setAdding(false); refetch();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  const revoke = async (k: ApiKey) => {
    if (!confirm(`Revoke "${k.name}"? ${k.source} can no longer send leads with it. This can't be undone — you'd create a new key.`)) return;
    try { await api.delete(`/api/admin/api-keys/${k.id}`); refetch(); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  const active = (keys || []).filter(k => !k.revoked_at);
  const revoked = (keys || []).filter(k => k.revoked_at);

  return (
    <>
      <div className="card set-card">
        <div className="set-card-head">
          <div>
            <div className="section-label">Website API keys</div>
            <div className="set-hint">
              info.saga-advisory.com and www.saga-advisory.com send newsletter sign-ups to <code>{base}/leads</code> with one of these keys.
              They arrive under <strong>Leads</strong> and as contacts. Full reference: <code>docs/LEADS_API.md</code> in the CRM repository.
            </div>
          </div>
          <button className="btn primary sm" onClick={() => { setAdding(true); setCreated(null); setForm({ name: "", source: SOURCES[0], scope: "server", origins: "" }); }}>
            {I.plus({ size: 13, w: 2 })}New key
          </button>
        </div>
        {err && <div className="set-notice err">{err}</div>}

        {adding && (
          <div className="set-form">
            <div className="set-grid">
              <label className="set-field">
                <span className="set-label">Name</span>
                <input autoFocus value={form.name} placeholder="e.g. info newsletter (production)" onChange={e => setForm({ ...form, name: e.target.value })} />
              </label>
              <label className="set-field">
                <span className="set-label">Source shown on leads</span>
                <input list="ak-sources" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} />
                <datalist id="ak-sources">{SOURCES.map(s => <option key={s} value={s} />)}</datalist>
              </label>
              <div className="set-field wide">
                <span className="set-label">How the site calls the CRM</span>
                <label className="set-check"><input type="radio" checked={form.scope === "server"} onChange={() => setForm({ ...form, scope: "server" })} />
                  <span><strong>From its server</strong> (recommended) — full access: send, look up, update and unsubscribe leads. Keep the key secret on the server.</span></label>
                <label className="set-check"><input type="radio" checked={form.scope === "form"} onChange={() => setForm({ ...form, scope: "form" })} />
                  <span><strong>Straight from a web form in the browser</strong> — can only send sign-ups, only from the sites listed below. Safe to put in page code.</span></label>
              </div>
              {form.scope === "form" && (
                <label className="set-field wide">
                  <span className="set-label">Websites allowed to use it</span>
                  <input value={form.origins} placeholder="https://www.saga-advisory.com https://saga-advisory.com" onChange={e => setForm({ ...form, origins: e.target.value })} />
                  <span className="set-hint">Exact site addresses (scheme + domain), separated by spaces. Include both www and non-www if the site uses both.</span>
                </label>
              )}
            </div>
            <div className="set-form-foot">
              <button className="btn ghost sm" onClick={() => setAdding(false)}>Cancel</button>
              <button className="btn primary sm" disabled={!form.name.trim() || !form.source.trim()} onClick={create}>Create key</button>
            </div>
          </div>
        )}

        {created && <CreatedKey k={created} base={base} onDone={() => setCreated(null)} />}

        <div className="set-rows">
          {active.map(k => (
            <div key={k.id} className="set-row">
              <span className="set-avatar" style={{ background: k.scope === "form" ? "var(--ember)" : undefined }}>{k.scope === "form" ? "F" : "S"}</span>
              <div className="set-row-main">
                <div className="set-row-title">
                  {k.name}
                  <span className={clsx("set-badge", k.scope === "server" ? "info" : "gold")}>{k.scope === "server" ? "Server key" : "Form key"}</span>
                </div>
                <div className="set-row-sub">
                  <code>{k.key_prefix}…</code> · {k.source}{k.allowed_origins.length ? ` · from ${k.allowed_origins.join(", ")}` : ""}
                </div>
                <div className="set-row-sub">
                  Last used {fmt(k.last_used_at)} · {k.use_count} call{k.use_count === 1 ? "" : "s"} · {k.lead_count} lead{k.lead_count === 1 ? "" : "s"} from {k.source}
                  {k.created_by_name ? ` · created by ${k.created_by_name}` : ""}
                </div>
              </div>
              <div className="set-row-actions">
                <button className="btn ghost sm set-danger" onClick={() => revoke(k)}>Revoke</button>
              </div>
            </div>
          ))}
          {keys && active.length === 0 && !adding && !created && (
            <div className="set-empty">No keys yet. Create one for each website, then give it to whoever runs that site.</div>
          )}
          {revoked.length > 0 && (
            <div className="set-group">
              <div className="set-group-label">Revoked</div>
              {revoked.map(k => (
                <div key={k.id} className="set-row muted">
                  <div className="set-row-main">
                    <div className="set-row-title">{k.name}</div>
                    <div className="set-row-sub"><code>{k.key_prefix}…</code> · {k.source} · revoked {fmt(k.revoked_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function CreatedKey({ k, base, onDone }: { k: NewKey; base: string; onDone: () => void }) {
  const curl = `curl -X POST ${base}/leads \\
  -H "Authorization: Bearer ${k.key}" \\
  -H "Content-Type: application/json" \\
  -d '{"email":"jane@example.com","name":"Jane Doe","company":"Example Energy","country":"Nigeria","interests":["LNG","Upstream"],"consent":true,"consent_text":"Yes, send me the Saga Africa Energy brief","source_detail":"Footer newsletter"}'`;
  const js = `await fetch("${base}/leads", {
  method: "POST",
  headers: { "Content-Type": "application/json", "Authorization": "Bearer ${k.key}" },
  body: JSON.stringify({ email, name, consent: true, consent_text, source_detail: "Newsletter form" }),
});`;
  const html = `<form action="${base}/leads" method="POST">
  <input type="hidden" name="api_key" value="${k.key}">
  <input type="hidden" name="redirect" value="${k.allowed_origins[0] || "https://www.saga-advisory.com"}/thanks">
  <input type="hidden" name="source_detail" value="Newsletter form">
  <input name="email" type="email" required placeholder="Email">
  <input name="name" placeholder="Name">
  <label><input type="checkbox" name="consent" value="true" required> Send me the newsletter</label>
  <input name="website" style="display:none" tabindex="-1" autocomplete="off">
  <button>Subscribe</button>
</form>`;
  return (
    <div className="set-form ak-created">
      <div className="set-notice ok" style={{ margin: "0 0 12px" }}>
        Key created. Copy it now — for security only a fingerprint is stored, so it can't be shown again.
      </div>
      <div className="ak-key"><code>{k.key}</code><Copy text={k.key} /></div>
      <div className="set-hint" style={{ margin: "10px 0 6px" }}>
        {k.scope === "server"
          ? "Give this to whoever runs the website. It belongs in the site's server settings, never in page code."
          : "This key may sit in the page; it only works from the websites listed and can only send sign-ups."}
      </div>
      <div className="ak-snippets">
        {k.scope === "server" ? (
          <>
            <div className="ak-snip"><div className="ak-snip-head"><span>Test from a terminal</span><Copy text={curl} /></div><pre>{curl}</pre></div>
            <div className="ak-snip"><div className="ak-snip-head"><span>From the site's backend (Node)</span><Copy text={js} /></div><pre>{js}</pre></div>
          </>
        ) : (
          <>
            <div className="ak-snip"><div className="ak-snip-head"><span>Plain HTML form</span><Copy text={html} /></div><pre>{html}</pre></div>
            <div className="ak-snip"><div className="ak-snip-head"><span>JavaScript form handler</span><Copy text={js} /></div><pre>{js}</pre></div>
          </>
        )}
      </div>
      <div className="set-form-foot"><button className="btn primary sm" onClick={onDone}>I've saved the key</button></div>
    </div>
  );
}
